import { useMemo, useState } from 'react';
import { generatePath, useSearchParams } from 'react-router-dom';

import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconBuilder } from '@components/icons/builder';
import { IconTrash } from '@components/icons/trash';
import { IconTraveller } from '@components/icons/traveller';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Table, TableProps, TableView } from '@components/table';
import { useTableStore } from '@components/table/table-store';
import { ViewsProvider } from '@components/table/toolbar/helpers';
import { useEvent } from '@hooks/use-event';
import { pxToRem } from '@utils/converters';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { useIsOutOfCredits } from 'api/authentication/hooks';
import { useCachedFavorites, useAddFavorite, useRemoveFavorite } from 'api/favorites/hooks';
import { CompanyUserFavorite } from 'api/favorites/requests';
import { useCreateAttribute, useRemoveAttribute } from 'api/journeys/hooks';
import { JourneyAttributeData, JourneyExtraState, JourneyTableView } from 'api/journeys/requests';
import { useTableViewSettings } from 'api/table-views/hooks';
import { useViewIdUrlSync } from 'api/table-views/use-view-id-url-sync';
import { useJourneyTravellersRaw } from 'api/traveller-journeys/hooks';
import { JourneyType, TravellerJourneyListItem } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';
import { useFieldFormSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form';

import {
  createBaseColumns,
  makeAttributeId,
  useActionColumns,
  useAttributeColumns,
  useDebouncedFieldsFetch,
} from './columns';
import { createExtraStateStore } from './extra-state-store';
import { t } from './locale';
import { useTravellersColumnControlStore } from './travellers-column-control-store';
import { useFilterConfig } from './use-filter-config';
import { useJourneyViews } from './use-journey-views';

import { useCompanyTravellerToJourneySidebar } from '../../travellers/company-traveller-to-journey';
import { CustomColumnControl } from '../custom-column-control';
import { TravellerOptionsMenu } from '../traveller-options-menu';

type JourneyTravellerListProps = Pick<TableProps, 'tableRef'> & {
  journey: JourneyType;
  attributes: Array<AttributeType | TravellerAttributeTemplate>;
  searchStr: string;
  setSelectedTravellers(travellers: Array<TravellerJourneyListItem>): void;
};

const useExtraStateStoreWithViews = (initialExtraState?: JourneyExtraState) => {
  return useState(() => createExtraStateStore(initialExtraState))[0];
};

export const TravellerJourneysTable = ({
  journey,
  tableRef,
  attributes,
  searchStr,
  setSelectedTravellers,
}: JourneyTravellerListProps) => {
  const [searchParams] = useSearchParams();
  const viewIdFromUrl = searchParams.get('viewId');

  const travellersQuery = useJourneyTravellersRaw(journey.shortId);
  const usersQuery = useUsers();
  const isOutOfCredits = useIsOutOfCredits();
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const showAddAttributeSidebar = useFieldFormSidebar();
  const createAttributeMutation = useCreateAttribute();
  const showDeleteModal = useDeleteModal();
  const removeAttributeMutation = useRemoveAttribute(journey.shortId);
  const handleFieldsNeeded = useDebouncedFieldsFetch(journey.shortId);

  const favorites = useCachedFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const {
    views: loadedViews,
    selectedViewId,
    isLoading: viewsLoading,
  } = useTableViewSettings({
    tableType: 'journey',
    tableObjectShortId: journey.shortId,
  });

  const initialView = useMemo(() => {
    if (!loadedViews?.length) return undefined;
    if (viewIdFromUrl) {
      const viewFromUrl = loadedViews.find((view) => view.id === viewIdFromUrl);
      if (viewFromUrl) return viewFromUrl as JourneyTableView;
    }
    return (
      (loadedViews.find((view) => view.id === selectedViewId) as JourneyTableView | undefined) ??
      (loadedViews[0] as JourneyTableView | undefined)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedViews === undefined]);

  const extraStateStore = useExtraStateStoreWithViews(initialView?.extraState);

  const tableStore = useTableStore({ selectedView: initialView });

  const activeAttributes = useMemo(() => {
    return attributes.filter((attribute) => attribute.isActive);
  }, [attributes]);

  const actionsData = journey.actionsData!;

  const { isLoading: configLoading, config } = useFilterConfig(
    activeAttributes,
    extraStateStore,
    tableStore,
    actionsData
  );

  const {
    views: rawViews,
    selectedView: rawSelectedView,
    hasUnsavedChanges,
    onPersist,
    createView,
    updateView,
    deleteView,
    selectView,
    reorderViews,
    saveView,
    discardChanges,
  } = useJourneyViews({
    journeyShortId: journey.shortId,
    views: loadedViews as JourneyTableView[] | undefined,
    selectedViewId,
    extraStateStore,
    tableStore,
    filterConfig: config,
  });

  const views = useMemo(() => {
    return rawViews.map((view) => ({
      ...view,
      favoriteItem: view.id
        ? favorites.find(
            (favorite) =>
              favorite.contentType === 'journeyview' && favorite.objectShortId === view.id
          )
        : undefined,
    }));
  }, [rawViews, favorites]);

  const selectedView = useMemo(() => {
    if (!rawSelectedView) {
      return undefined;
    }
    return views.find((view) => view.id === rawSelectedView.id);
  }, [rawSelectedView, views]);

  useViewIdUrlSync({ selectedViewId: selectedView?.id, views, selectView });

  const onToggleFavorite = useEvent((view: TableView<CompanyUserFavorite>) => {
    const viewId = view.id ?? '';

    if (view.favoriteItem) {
      removeFavorite.mutate(view.favoriteItem.shortId);
    } else {
      addFavorite.mutate({
        contentType: 'journeyview',
        objectShortId: viewId,
        journeyShortId: journey.shortId,
      });
    }
  });

  const columnControlStore = useTravellersColumnControlStore(
    tableStore,
    extraStateStore,
    handleFieldsNeeded
  );

  const { canManageJourneyTravellers, canEditJourneyTravellerData } = usePermissions();
  const attributeColumns = useAttributeColumns({
    attributes: activeAttributes,
    usersData: usersQuery.data,
    showEditTravellerSidebar,
    editableJourneyAttributes: canEditJourneyTravellerData,
    editableCompanyTravellerAttributes: canManageJourneyTravellers,
  });

  const actionColumns = useActionColumns({
    actionsData: journey.actionsData,
    extraStateStore,
    tableStore,
    onPersist,
    columnControlStore,
  });

  const availableColumns = useMemo(() => {
    return createBaseColumns().concat(attributeColumns).concat(actionColumns);
  }, [attributeColumns, actionColumns]);

  const parsedTravellers = useMemo(() => {
    return (travellersQuery.data ?? []).map((traveller) => {
      const dynamicValues = attributes.reduce((accumulated, attribute) => {
        const attributeId = makeAttributeId(
          attribute.shortId,
          Boolean(attribute.isTravellerAttribute)
        );
        const attributeArray = attribute.isTravellerAttribute
          ? traveller.companyTravellerAttributes
          : traveller.journeyAttributes;

        const travellerAttribute = attributeArray.find(
          (journeyAttribute) => journeyAttribute.attributeShortId === attribute.shortId
        );

        if (attribute.attributeType === 'multi_select') {
          accumulated[attributeId] = travellerAttribute?.multiOptionValues;
          return accumulated;
        }

        let attributeValue = travellerAttribute?.value;
        if (!attributeValue) {
          const files = travellerAttribute?.files;
          if (files?.length) {
            attributeValue = files;
          }
        }

        accumulated[attributeId] = attributeValue;

        return accumulated;
      }, {} as Record<string, any>);

      return {
        ...traveller,
        status: isOutOfCredits && traveller.status === 'in_progress' ? 'paused' : traveller.status,
        ...dynamicValues,
        ...traveller.actionsData,
      };
    });
  }, [attributes, travellersQuery.data, isOutOfCredits]);

  const endFixedColumns: TableProps<TravellerJourneyListItem>['endFixedColumns'] = useMemo(() => {
    return [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: TravellerOptionsMenu,
        renderHeaderIcon: () => (
          <IconButton
            style={{ height: pxToRem(40), width: pxToRem(40) }}
            icon={<IconAddPlus />}
            variant="ghost-purple"
            onClick={() =>
              showAddAttributeSidebar({
                isTravellerAttribute: false,
                onCreate: async (data: JourneyAttributeData) => {
                  const response = await createAttributeMutation.mutateAsync({
                    journeyShortId: journey.shortId,
                    data,
                  });
                  columnControlStore.actions.toggleColumn(
                    makeAttributeId(response.shortId, false),
                    true
                  );
                  return response;
                },
              })
            }
          />
        ),
      },
    ];
  }, [
    columnControlStore.actions,
    createAttributeMutation,
    journey.shortId,
    showAddAttributeSidebar,
  ]);

  if (travellersQuery.isLoading || usersQuery.isLoading || viewsLoading) {
    return <Spinner />;
  }

  const additionalHeaderMenuItems = (fieldId: string) => {
    const isJourneyAttribute = fieldId.startsWith('journey-attribute');
    if (!isJourneyAttribute) return null;
    const shortId = fieldId.split('-')[2];
    return (
      <Menu.Item
        label={t('core.delete')}
        variant="danger"
        icon={<IconTrash color="red100" />}
        onSelect={() =>
          showDeleteModal({
            title: t('core.delete'),
            description: t('areYouSureRemove'),
            hasUserConfirmation: true,
            buttonText: t('core.delete'),
            buttonIcon: <IconTrash />,
            onConfirm: () => {
              removeAttributeMutation.mutateAsync(shortId);
            },
          })
        }
      />
    );
  };

  return (
    <ViewsProvider
      views={views}
      selectedView={selectedView}
      createView={createView}
      updateView={updateView}
      deleteView={deleteView}
      selectView={selectView}
      reorderViews={reorderViews}
      onToggleFavorite={onToggleFavorite}
      hasUnsavedChanges={hasUnsavedChanges}
      saveView={saveView}
      discardChanges={discardChanges}
    >
      <Table<TravellerJourneyListItem>
        idColumn="shortId"
        searchStr={searchStr}
        tableRef={tableRef}
        data={parsedTravellers}
        availableColumns={availableColumns}
        endFixedColumns={endFixedColumns}
        store={tableStore}
        onSelectChange={setSelectedTravellers}
        additionalHeaderMenuItems={additionalHeaderMenuItems}
        hasViews
        emptyState={
          <EmptyState
            icon={<IconTraveller />}
            header={t('emptyHeader')}
            content={t('emptyContent')}
            action={
              <Button
                variant="primary"
                iconLeft={<IconBuilder color="white100" />}
                text={t('openBuilder')}
                fitContent
                href={generatePath(routePaths.editJourney, {
                  workspaceId: journey.workspace,
                  journeyId: journey.shortId,
                  '*': '',
                })}
              />
            }
          />
        }
        filterProps={{
          addFilterText: t('core.filter'),
          config,
          isLoading: configLoading,
        }}
        columnControl={
          <CustomColumnControl
            availableColumns={availableColumns}
            tableStore={tableStore}
            extraStateStore={extraStateStore}
            actionsData={actionsData}
            columnControlStore={columnControlStore}
          />
        }
      />
    </ViewsProvider>
  );
};
